import { IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type { AttachmentDrop } from "@/types/AttachmentDrop";
import { isIntentionalDropReason } from "@/types/AttachmentDrop";

interface AttachmentDropsBlockProps {
  drops?: AttachmentDrop[] | null;
}

/**
 * Sent-mail detail: which attachments this email went out WITHOUT, and why.
 * Failures first (error tone: wallet icon/signing, calendar start date, fetch/
 * size guards), then intentional targeting/config exclusions (info tone).
 * Send-time composition facts, NOT SES delivery events — hence its own block
 * in the left column, not an EmailDeliveryEventCard. Renders nothing when the
 * email carried all its attachments (or the role has the detail masked).
 */
export const AttachmentDropsBlock: React.FC<AttachmentDropsBlockProps> = ({
  drops,
}) => {
  const t = useTranslations("attachmentDropReason");

  if (!drops || drops.length === 0) return null;

  // `t.has` fallback: unknown future backend codes get the generic label.
  const reasonLabel = (code: string) => (t.has(code) ? t(code) : t("unknown"));

  const failures = drops.filter((d) => !isIntentionalDropReason(d.reason));
  const intentional = drops.filter((d) => isIntentionalDropReason(d.reason));

  const renderRow = (d: AttachmentDrop, i: number, isError: boolean) => {
    const Icon = isError ? IconAlertTriangle : IconInfoCircle;
    // filename can be empty (whole-resolve failure) — reason-first then.
    const label = d.attachmentName || d.filename;
    return (
      <div
        key={`${d.reason}-${d.filename}-${i}`}
        className={`border rounded-md px-3 py-2 flex items-center gap-2 ${
          isError
            ? "surface-error outline-error"
            : "surface-secondary outline-quaternary"
        }`}
      >
        <Icon
          size={14}
          className={`shrink-0 ${isError ? "font-error" : "font-tertiary"}`}
        />
        {label && (
          <span
            className={`body-3-medium truncate ${
              isError ? "font-error" : "font-secondary"
            }`}
          >
            {label}
          </span>
        )}
        <span
          className={`body-3-regular flex-1 min-w-0 truncate ${
            isError ? "font-error" : "font-tertiary"
          }`}
        >
          {reasonLabel(d.reason)}
        </span>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2 mb-4 max-h-56 overflow-y-auto">
      <div className="flex items-center gap-1.5">
        <IconAlertTriangle
          size={14}
          className={`shrink-0 ${failures.length > 0 ? "font-error" : "font-tertiary"}`}
        />
        <p
          className={`body-3-medium ${failures.length > 0 ? "font-error" : "font-secondary"}`}
        >
          {t("sentDegradedTitle", { count: drops.length })}
        </p>
      </div>
      {failures.map((d, i) => renderRow(d, i, true))}
      {intentional.map((d, i) => renderRow(d, i, false))}
    </div>
  );
};
